import { format, isValid } from 'date-fns';
import omit from 'lodash-es/omit';
import {
  ApplicantStatus,
  ApplicantType,
  ApplicationStep,
  BUSINESS_LOAN_PRODUCTS,
  EmploymentType,
  EnterpriseStructure,
  FINANCIAL_PRODUCT,
  OwnershipType,
  PaymentFunds,
  isExtendedStructure,
} from '@brighte/validation';
import { datadogRum } from '@datadog/browser-rum';
import {
  ApplicationFormStatus,
  CreateApplicationFromQuoteInput,
  CreateApplicationProps,
  EnterpriseStructure as EnterpriseStructureType,
  FinancialProduct,
  FormValues,
  RelatedReferralWithSummary,
  TermsAndConditionsInput,
  UpdateApplicationInput,
  ApplicantStatus as UpdateApplicationInputApplicantStatus,
  ApplicantType as UpdateApplicationInputApplicantType,
  EmploymentType as UpdateApplicationInputEmploymentType,
  UpdateApplicationProps,
} from '../../graphql';
import { actCommunityTermsList, getTermsInputByFinancialProduct, smeTermsList } from '../../steps/Eligibility/helpers';
import { getRepaymentDetails } from '../../steps/FinancePurchase/utils/helpers';
import {
  APPLICATION_STEPS,
  BUSINESS_DETAILS,
  ELECTRIFY_CASH_REPAYMENT_TERMS,
  ELIGIBILITY,
  REPAYMENT_ACCOUNT,
  SUMMARY,
  extractStringFromComponent,
  getAdminUserId,
  getApplicantTypeAndTrackingIdFromUrl,
  isJointApplicant,
  pascalToCamelCase,
} from '../../utils';

//Generates JSON that contains all the terms based on the financialProduct
export const transformTerms = ({
  agreedToAll,
  financialProduct,
  isCashApplication,
  businessRoleDeclaration,
}: {
  agreedToAll: boolean;
  financialProduct: FinancialProduct;
  isCashApplication: boolean;
  businessRoleDeclaration?: string;
}): TermsAndConditionsInput | null => {
  if (!agreedToAll) {
    datadogRum.addError(new Error('Eligibility terms not agreed'));
    return null;
  }
  const terms = getTermsInputByFinancialProduct(financialProduct, isCashApplication);
  const {
    FINANCE_TERMS,
    PRIVACY_TERMS,
    SFA_TERMS,
    CUSTOMER_DECLARATION_DESCRIPTION,
    DECLARATION_CREDIT_BUSINESS,
    CONSENT_MARKETING_INFO,
    SME_TERMS_AND_CONDITIONS,
    ACT_COMMUNITY_TERMS_AND_CONDITIONS,
    ACT_COMMUNITY_SHS_CONSENT,
  } = ELIGIBILITY;

  const isBhesl = financialProduct.id === FINANCIAL_PRODUCT.BHESL;

  const filteredTerms = terms.reduce((prev, item) => {
    const TERMS_MAP = {
      ...FINANCE_TERMS,
      ...PRIVACY_TERMS,
      ...SFA_TERMS,
      ...(isBhesl ? { beOver18: FINANCE_TERMS.beOver18AustralianCitizen } : {}),
    };
    const node = TERMS_MAP[item];
    const propNameMapping = {
      beActHomeOwner: 'beGovSchemeHomeOwner',
      beTasHomeOwner: 'beGovSchemeHomeOwner',
      consentActTerms: 'consentGovSchemeTerms',
      consentTasTerms: 'consentGovSchemeTerms',
      consentNswTerms: 'consentGovSchemeTerms',
      haveAustralianIncomeWithoutRetired: 'haveAustralianIncome',
      consentFlexCreditCheck: 'consentCreditCheck',
      creditAndTermsFlexAgreement: 'creditAndTermsAgreement',
      consentCashCreditReport: 'consentCreditReport',
      consentElectrify2515Terms: 'enterprisePartnerTerms',
      beActHomeOwnerAndOccupant: 'beGovSchemeHomeOwner',
      creditAndTermsBNPLAgreement: 'creditAndTermsAgreement',
      creditAndTermsBheslAgreement: 'creditAndTermsAgreement',
      consentCreditReportBNPL: 'consentCreditReport',
      haveReadDisclosureOfCreditInformation: 'haveReadDisclosureOfCreditInformation',
      beCompletedPropertyOwner: 'beCompletedPropertyOwner',
    };

    const propName = propNameMapping[item] || item;
    prev[propName] = typeof node === 'string' ? node : extractStringFromComponent(node);

    return prev;
  }, {});

  const isBusiness = BUSINESS_LOAN_PRODUCTS.includes(financialProduct.id);

  if (isBusiness) {
    const termsList = financialProduct.id === FINANCIAL_PRODUCT.ACT_COMMUNITY ? actCommunityTermsList : smeTermsList;
    filteredTerms['businessEligibilityCriteriaAgreement'] = termsList
      .map(term => {
        const node = FINANCE_TERMS[term];
        return typeof node === 'string' ? node : extractStringFromComponent(node);
      })
      .filter(Boolean)
      .join('; ');
  }

  // For business products, always include consentCustomerDeclaration. When the enterprise
  // structure is already known (businessRoleDeclaration set), include the role text as the
  // first part. When unknown (e.g. Eligibility runs before Business Details), omit the role
  // prefix — Business Details will overwrite with the complete declaration once submitted.
  const isActCommunity = financialProduct.id === FINANCIAL_PRODUCT.ACT_COMMUNITY;
  const termsAndConditionsNode = isActCommunity ? ACT_COMMUNITY_TERMS_AND_CONDITIONS : SME_TERMS_AND_CONDITIONS;
  const customerDeclaration = isBusiness
    ? [
        ...(businessRoleDeclaration ? [businessRoleDeclaration] : []),
        DECLARATION_CREDIT_BUSINESS,
        CONSENT_MARKETING_INFO,
        extractStringFromComponent(termsAndConditionsNode),
        ...(isActCommunity ? [extractStringFromComponent(ACT_COMMUNITY_SHS_CONSENT)] : []),
      ].join('; ')
    : CUSTOMER_DECLARATION_DESCRIPTION;

  return {
    ...filteredTerms,
    consentCustomerDeclaration: customerDeclaration,
  };
};

export function getApplicantType(applicantType: ApplicantType) {
  return getEnumKey<UpdateApplicationInputApplicantType, typeof ApplicantType>(applicantType, ApplicantType);
}

function excludeFullAddressFromCollection(data) {
  return data.map(item => {
    // If item has no address field, return as is
    if (!item.address) {
      return item;
    }

    const {
      address: { fullAddress, ...restAddress },
      ...rest
    } = item;
    return {
      ...rest,
      address: {
        ...restAddress,
      },
    };
  });
}

export function getEnumKey<T, K extends object>(value: unknown, sourceEnum: K) {
  return Object.keys(sourceEnum)[Object.values(sourceEnum).indexOf(value)] as T;
}

function getTermsByFinancialProduct({
  eligibilityFormValues,
  financialProduct,
  isCashApplication = false,
  businessRoleDeclaration,
}: {
  eligibilityFormValues: FormValues['Eligibility'];
  financialProduct: FinancialProduct;
  isCashApplication?: boolean;
  businessRoleDeclaration?: string;
}) {
  const { id: financialProductId } = financialProduct;

  let agreedToAllTerms = false;
  const businessProducts = [FINANCIAL_PRODUCT.ACT_SFA, ...BUSINESS_LOAN_PRODUCTS];
  if (businessProducts.includes(financialProductId)) {
    agreedToAllTerms = eligibilityFormValues?.agreeToFinanceTerms && eligibilityFormValues?.agreeToCustomerDeclaration;
  } else {
    const privacyTerms = eligibilityFormValues?.agreeToPrivacyTerms ?? false;
    agreedToAllTerms =
      eligibilityFormValues?.agreeToFinanceTerms && eligibilityFormValues?.agreeToCustomerDeclaration && privacyTerms;
  }

  return transformTerms({
    agreedToAll: agreedToAllTerms,
    financialProduct,
    isCashApplication,
    businessRoleDeclaration,
  });
}

function transformCreateApplicationInput({
  formValues,
  user,
  quote,
}: CreateApplicationProps): CreateApplicationFromQuoteInput | null {
  const { Finance, Eligibility, ContactDetails } = formValues;

  if (!quote?.financialProduct || !Finance || !Eligibility || !ContactDetails) {
    const missingFields: string[] = [];
    if (!quote?.financialProduct) missingFields.push('Quote-financialProduct');
    if (!Finance) missingFields.push('Finance');
    if (!Eligibility) missingFields.push('Eligibility');
    if (!ContactDetails) missingFields.push('ContactDetails');

    const logMessage = `Missing required form values to create application input: ${missingFields.join(', ')}`;
    datadogRum.addAction('Invalid create application input', {
      name: 'Create Application Input Error',
      values: {
        financialProduct: quote?.financialProduct,
        finance: Finance,
        eligibility: Eligibility,
        contactDetails: ContactDetails,
      },
    });
    datadogRum.addError(new Error(logMessage));
    return null;
  }

  const { financialProductId } = Eligibility;
  const term = Finance.repaymentTerm;

  const isCashApplication = Finance?.payment?.isCashApplication;

  const { trackingId, applicantType } = getApplicantTypeAndTrackingIdFromUrl();

  const repaymentTerm = isCashApplication ? ELECTRIFY_CASH_REPAYMENT_TERMS : getRepaymentDetails(term, quote);
  const terms = getTermsByFinancialProduct({
    eligibilityFormValues: Eligibility,
    financialProduct: quote?.financialProduct,
    isCashApplication,
  });

  const { given_name, family_name } = user || {};

  if (!terms || !given_name || !family_name || !repaymentTerm) {
    const missingFields: string[] = [];
    if (!term) missingFields.push('selected term');
    if (!terms) missingFields.push('repayment term object');
    if (!given_name) missingFields.push('given name from user');
    if (!family_name) missingFields.push('family name from user');
    if (!repaymentTerm) missingFields.push('repaymentTerm');

    const logMessage = `Missing required values to create application input: ${missingFields.join(', ')}`;

    datadogRum.addAction('Invalid create application input', {
      name: 'Create Application Input Error',
      values: {
        financialProductId,
        repaymentTerm,
        terms,
      },
    });

    datadogRum.addError(new Error(logMessage));
    return null;
  }

  const supportMultipleCategories = quote?.productConfiguration?.supportMultipleCategories ?? false;
  const relatedQuotesInput = supportMultipleCategories
    ? (quote?.relatedReferrals as RelatedReferralWithSummary[])?.map(relatedQuote => ({
        id: relatedQuote.id as string,
        repaymentTerm: isCashApplication ? ELECTRIFY_CASH_REPAYMENT_TERMS : getRepaymentDetails(term, relatedQuote),
      }))
    : undefined;

  datadogRum.addAction('Before creating payload');

  const payload = {
    id: trackingId,
    status: ApplicationFormStatus.Draft,
    step: ApplicationStep.CreateApplication,
    financialProductId,
    repaymentTerm,
    customerType: quote.financialProduct.customerType,

    address: {
      unitNumber: Finance.address.unitNumber,
      streetNumber: Finance.address.streetNumber ?? '',
      streetName: Finance.address.streetName ?? '',
      suburb: Finance.address.suburb,
      state: Finance.address.state,
      postcode: Finance.address.postcode,
    },

    applicant: {
      firstName: user?.given_name ?? '',
      lastName: user?.family_name ?? '',
      type: getApplicantType(applicantType),
      ...(Eligibility?.applicant && Eligibility.applicant.unsolicitedSaleOptions
        ? {
            unsolicitedSaleOptions: Eligibility.applicant.unsolicitedSaleOptions,
          }
        : {}),
      termsAndConditions: {
        ...terms,
      },
      ...(financialProductId === FINANCIAL_PRODUCT.BHESL
        ? {
            sresClaimed: Eligibility?.sresClaimed,
            nswSafeguardClaimed: Eligibility?.nswSafeguardClaimed,
            hesdClaimed: Eligibility?.hesdClaimed,
            nathersAssessment: Eligibility?.nathersAssessment,
            replacesExistingEquipment: Eligibility?.replacesExistingEquipment,
          }
        : {}),
      ownProperty: Finance?.applicant?.ownProperty,
      nonHomeOwnerCheck: Finance?.applicant?.nonHomeOwnerCheck,
      isCurrentAddress: Finance?.applicant?.isCurrentAddress,
      ...(Finance?.applicant?.isCurrentAddress === false
        ? {
            previousAddress: {
              unitNumber: Finance?.applicant?.previousAddress?.unitNumber,
              streetNumber: Finance?.applicant?.previousAddress?.streetNumber ?? '',
              streetName: Finance?.applicant?.previousAddress?.streetName ?? '',
              suburb: Finance?.applicant?.previousAddress?.suburb,
              state: Finance?.applicant?.previousAddress?.state,
              postcode: Finance?.applicant?.previousAddress?.postcode,
            },
          }
        : {}),
    },
    payment: {
      isCashApplication: Finance?.payment.isCashApplication,
    },
    ...(relatedQuotesInput && { relatedQuotesInput }),
  };

  datadogRum.addAction('Transformed create application input');
  return payload;
}

function transformCreateJointApplicationInput({ formValues }: CreateApplicationProps) {
  const { trackingId } = getApplicantTypeAndTrackingIdFromUrl();

  return {
    applicationId: trackingId,
    status: ApplicationFormStatus.Draft,
    type: getApplicantType(ApplicantType.Joint),
    ...(formValues?.HouseholdDetails?.hasJointApplicant && {
      ...formValues?.HouseholdDetails?.jointApplicant,
    }),
  };
}

// A DatePicker value is a JS Date at LOCAL midnight; JSON-serialising it (toISOString)
// shifts a date-only field back a day in +UTC timezones (e.g. 08/10 AEST → 07/10 UTC).
// Format to a plain local calendar date so the day the user picked is preserved on the wire.
const toCalendarDate = (value?: Date | string | null): string | undefined => {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  return isValid(date) ? format(date, 'yyyy-MM-dd') : typeof value === 'string' ? value : undefined;
};

function transformUpdateApplicationInput({
  currentPage,
  formValues,
}: UpdateApplicationProps): UpdateApplicationInput | null {
  type EnterpriseStructureInput = NonNullable<NonNullable<UpdateApplicationInput['enterprise']>['structure']>;
  const { quote } = formValues;

  if (!quote) {
    datadogRum.addError(new Error('Quote is missing in form values'));
    return null;
  }
  const { trackingId, applicantType } = getApplicantTypeAndTrackingIdFromUrl();
  const isJoint = isJointApplicant();

  const updateApplicationInput: UpdateApplicationInput = {
    id: trackingId,
    step: currentPage,
    status: ApplicationFormStatus.Draft,
    financialProductId: quote.financialProduct.id ?? '',
    customerType: quote.financialProduct.customerType,
  };

  const stepFormValues = formValues[currentPage];
  if (!stepFormValues) {
    datadogRum.addError(new Error(`${currentPage} step form values are missing`));
    return null;
  }

  switch (currentPage) {
    case APPLICATION_STEPS.ELIGIBILITY: {
      const businessDetailsFormValues = formValues[APPLICATION_STEPS.BUSINESS_DETAILS];
      const enterpriseStructure = (
        businessDetailsFormValues?.enterprise as { structure?: EnterpriseStructure } | undefined
      )?.structure;

      const BUSINESS_DECLARATION_BY_STRUCTURE_ELIGIBILITY: Partial<Record<EnterpriseStructure, string>> = {
        [EnterpriseStructure.SoleTrader]: BUSINESS_DETAILS.DECLARATION_SOLE_TRADER,
        [EnterpriseStructure.Partnership]: BUSINESS_DETAILS.DECLARATION_PARTNER,
        [EnterpriseStructure.Trust]: BUSINESS_DETAILS.DECLARATION_TRUSTEE,
        [EnterpriseStructure.ProprietaryLimitedCompany]: BUSINESS_DETAILS.DECLARATION_DIRECTOR,
      };

      const businessRoleDeclaration = enterpriseStructure
        ? BUSINESS_DECLARATION_BY_STRUCTURE_ELIGIBILITY[enterpriseStructure]
        : undefined;

      const terms = getTermsByFinancialProduct({
        eligibilityFormValues: stepFormValues,
        financialProduct: quote.financialProduct,
        businessRoleDeclaration,
      });

      return {
        ...updateApplicationInput,
        step: isJoint ? ApplicationStep.JointEligibility : currentPage,
        applicant: {
          type: getApplicantType(applicantType),
          termsAndConditions: terms,
          ...(stepFormValues?.applicant && stepFormValues?.applicant.unsolicitedSaleOptions
            ? {
                unsolicitedSaleOptions: stepFormValues.applicant.unsolicitedSaleOptions,
              }
            : {}),
          ...(quote.financialProduct.id === FINANCIAL_PRODUCT.BHESL
            ? {
                sresClaimed: stepFormValues?.sresClaimed,
                nswSafeguardClaimed: stepFormValues?.nswSafeguardClaimed,
                hesdClaimed: stepFormValues?.hesdClaimed,
                nathersAssessment: stepFormValues?.nathersAssessment,
                replacesExistingEquipment: stepFormValues?.replacesExistingEquipment,
              }
            : {}),
          ownProperty: formValues?.Finance?.applicant?.ownProperty,
        },
      };
    }
    case APPLICATION_STEPS.IDENTIFICATION: {
      const { applicant, documents, isSkipDocuments } = stepFormValues;
      const applicantType = getApplicantType(applicant.type);
      const { fullAddress, ...address } = applicant.address;
      const applicationInput = { ...updateApplicationInput };

      return {
        ...applicationInput,
        applicant: {
          ...applicant,
          type: applicantType,
          address,
          // Serialise DOB as a local calendar date so the picked day isn't shifted by the
          // Date → UTC-ISO conversion (see toCalendarDate).
          ...(applicant.dateOfBirth ? { dateOfBirth: toCalendarDate(applicant.dateOfBirth) } : {}),
          identification: {
            ...applicant.identification,
            // Driving-licence / passport expiry come from a DatePicker (Date) — serialise as a
            // local calendar date, same reason as DOB. Medicare expiry is a mm/yyyy string, left as-is.
            ...(applicant.identification?.drivingLicense?.expiryDate && {
              drivingLicense: {
                ...applicant.identification.drivingLicense,
                expiryDate: toCalendarDate(applicant.identification.drivingLicense.expiryDate),
              },
            }),
            ...(applicant.identification?.passport?.expiryDate && {
              passport: {
                ...applicant.identification.passport,
                expiryDate: toCalendarDate(applicant.identification.passport.expiryDate),
              },
            }),
            ...(documents ? { documentIds: documents?.map(doc => doc.documentId) } : []),
            ...(isSkipDocuments ? { isSkipDocuments: true } : {}),
          },
        },
      };
    }

    case APPLICATION_STEPS.INCOME_VERIFICATION: {
      const applicant = stepFormValues.applicant;
      const primary = applicant.income?.primary;
      const isAdditionalIncome = applicant.income?.isAdditionalIncome;
      const primaryIncome = omit(primary, ['bankStatementComplete']);
      const applicantType = getApplicantType(applicant.type);
      const { incomeVerification } = stepFormValues;

      const applicationInput = { ...updateApplicationInput };
      const employmentType = applicant?.employment?.type
        ? getEnumKey<UpdateApplicationInputEmploymentType, typeof EmploymentType>(
            applicant.employment.type,
            EmploymentType,
          )
        : null;

      const additional = isAdditionalIncome
        ? (({ bankStatementComplete, ...additionalIncome }) => additionalIncome)(applicant.income.additional)
        : undefined;

      //pass only the documentIds in the payload
      if (primaryIncome.documents) {
        primaryIncome['documentIds'] = primaryIncome.documents.map(doc => doc.documentId);
        delete primaryIncome.documents;
        if (isAdditionalIncome) {
          additional['documentIds'] = additional.documents.map(doc => doc.documentId);
          delete additional.documents;
        }
      }

      return {
        ...applicationInput,
        applicant: {
          ...omit(applicant, ['financeAmount', 'income']),
          type: applicantType,
          ...(employmentType && {
            employment: {
              ...applicant.employment,
              type: employmentType,
            },
          }),
          ...(!applicant?.usingOpenBanking && {
            income: {
              ...applicant.income,
              primary: primaryIncome,
              ...(isAdditionalIncome && {
                additional,
              }),
              // Only include incomeVerification when it is explicitly marked as 'Failed'.
              // Omitting it for all other values avoids sending stale failure state to the server.
              ...(incomeVerification === 'Failed' ? { incomeVerification } : {}),
            },
          }),

          // just apply CDR flag if the financial product is Brighte Pay
          ...(quote.financialProduct.id === FINANCIAL_PRODUCT.BRIGHTE_PAY &&
            applicant.usingOpenBanking !== undefined && {
              usingOpenBanking: !!applicant.usingOpenBanking,
            }),
        },
      };
    }

    case APPLICATION_STEPS.HOUSEHOLD: {
      const applicant = stepFormValues.applicant;
      const applicantType = getApplicantType(applicant.type);

      let incomeData = {};
      if (applicant.income?.primary) {
        const incomeVerification = formValues?.IncomeVerification?.applicant?.income?.primary;
        const primary = applicant.income.primary;
        const primaryIncome = omit(primary, ['bankStatementComplete']);

        // Pass only the documentIds in the payload
        if (primaryIncome.documents) {
          primaryIncome['documentIds'] = primaryIncome.documents.map(doc => doc.documentId);
          delete primaryIncome.documents;
        }

        incomeData = {
          ...applicant.income,
          primary: {
            ...primaryIncome,
            amount: incomeVerification?.amount,
            period: incomeVerification?.period,
          },
        };
      }

      return {
        ...updateApplicationInput,
        hasJointApplicant: stepFormValues?.hasJointApplicant ?? false,
        isCashApplication: stepFormValues?.payment?.isCashApplication,
        applicant: {
          ...applicant,
          type: applicantType,
          ...(Object.keys(incomeData).length > 0 && { income: incomeData }),
        },
      };
    }

    case APPLICATION_STEPS.BUSINESS_DETAILS: {
      const { enterprise, applicant, customerDeclared } = stepFormValues;

      const structure = getEnumKey(enterprise.structure, EnterpriseStructure);
      const applicantType = getApplicantType(applicant.type);

      if (isExtendedStructure(enterprise.structure)) {
        const { fullAddress: primaryContactFullAddress, ...primaryContactAddress } =
          enterprise.primaryContact?.residentialAddress ?? {};

        const { fullAddress: billingFullAddress, ...billingAddress } =
          enterprise.billingContact?.residentialAddress ?? {};
        const billingContactPayload =
          !enterprise.billingContactSameAsPrimary && enterprise.billingContact
            ? {
                ...enterprise.billingContact,
                residentialAddress: billingAddress,
              }
            : undefined;

        const BUSINESS_DECLARATION_BY_STRUCTURE: Partial<Record<EnterpriseStructure, string>> = {
          [EnterpriseStructure.SoleTrader]: BUSINESS_DETAILS.DECLARATION_SOLE_TRADER,
          [EnterpriseStructure.Partnership]: BUSINESS_DETAILS.DECLARATION_PARTNER,
          [EnterpriseStructure.Trust]: BUSINESS_DETAILS.DECLARATION_TRUSTEE,
          [EnterpriseStructure.ProprietaryLimitedCompany]: BUSINESS_DETAILS.DECLARATION_DIRECTOR,
        };

        const {
          DECLARATION_CREDIT_BUSINESS,
          CONSENT_MARKETING_INFO,
          SME_TERMS_AND_CONDITIONS: smeTermsNode,
          ACT_COMMUNITY_TERMS_AND_CONDITIONS: actCommunityTermsNode,
          ACT_COMMUNITY_SHS_CONSENT: actShsConsentNode,
        } = ELIGIBILITY;
        const isBusiness = BUSINESS_LOAN_PRODUCTS.includes(quote.financialProduct.id);
        const isActCommunityProduct = quote.financialProduct.id === FINANCIAL_PRODUCT.ACT_COMMUNITY;
        const termsNode = isActCommunityProduct ? actCommunityTermsNode : smeTermsNode;
        const roleDeclaration =
          BUSINESS_DECLARATION_BY_STRUCTURE[enterprise.structure] ?? BUSINESS_DETAILS.DECLARATION_SOLE_TRADER;
        const consentCustomerDeclaration = isBusiness
          ? [
              roleDeclaration,
              DECLARATION_CREDIT_BUSINESS,
              CONSENT_MARKETING_INFO,
              extractStringFromComponent(termsNode),
              ...(isActCommunityProduct ? [extractStringFromComponent(actShsConsentNode)] : []),
            ].join('; ')
          : roleDeclaration;

        return {
          ...updateApplicationInput,
          applicant: {
            ...applicant,
            type: applicantType,
            ...(customerDeclared && {
              termsAndConditions: {
                consentCustomerDeclaration,
              },
            }),
          },
          enterprise: {
            structure,
            legalEntityName: enterprise.legalEntityName,
            tradingName: enterprise.tradingName,
            abn: enterprise.abn,
            businessPhone: enterprise.businessPhone,
            businessEmail: enterprise.businessEmail,
            businessWebsite: enterprise.businessWebsite,
            yearsInTrading: enterprise.yearsInTrading,
            numberOfEmployees: enterprise.numberOfEmployees,
            industryCategory: enterprise.industryCategory,
            propertyStatus: enterprise.propertyStatus,
            primaryContact: {
              ...enterprise.primaryContact,
              residentialAddress: primaryContactAddress,
            },
            billingContactSameAsPrimary: enterprise.billingContactSameAsPrimary,
            ...(billingContactPayload && { billingContact: billingContactPayload }),
            ...(enterprise.structure === EnterpriseStructure.ProprietaryLimitedCompany && enterprise.acnNumber
              ? { acnNumber: enterprise.acnNumber }
              : {}),
            ...(enterprise.structure === EnterpriseStructure.Trust && enterprise.trustDeedDocumentIds
              ? {
                  trustDeedDocumentIds: enterprise.trustDeedDocumentIds,
                  ...(enterprise.trustDeedDocumentNames && {
                    trustDeedDocumentNames: enterprise.trustDeedDocumentNames,
                  }),
                }
              : {}),
          },
        };
      }

      const { fullAddress, ...registeredAddress } = enterprise.registeredAddress;
      const { fullAddress: principalFullAddress, ...principalBusinessAddress } =
        enterprise.principalBusinessAddress ?? {};

      return {
        ...updateApplicationInput,
        applicant: {
          ...applicant,
          type: applicantType,
        },
        enterprise: {
          ...enterprise,
          structure,
          registeredAddress,
          principalBusinessAddress: structure === EnterpriseStructure.Company ? principalBusinessAddress : undefined,
        },
      };
    }

    case APPLICATION_STEPS.OWNERSHIP_DETAILS: {
      const { applicant, enterprise } = stepFormValues;

      const structure = getEnumKey<EnterpriseStructure, typeof EnterpriseStructure>(
        enterprise.structure,
        EnterpriseStructure,
      );
      const type = getApplicantType(applicant.type);

      const structureValue = enterprise.structure;
      // removes fullAddress from address by structure
      const structureProp = pascalToCamelCase(structure);
      let enterpriseValues = { ...enterprise };
      const enterpriseFields = enterpriseValues[structureProp];
      const directorsValue = enterpriseFields.directors
        ? excludeFullAddressFromCollection(enterpriseFields.directors)
        : undefined;

      if (structureValue === EnterpriseStructure.Company) {
        enterpriseValues = {
          ...enterpriseValues,
          [structureProp]: {
            ...enterpriseFields,
            ...(enterpriseFields.ownershipType === OwnershipType.Proprietary && {
              beneficialOwners: excludeFullAddressFromCollection(enterpriseFields.beneficialOwners),
            }),
          },
        };
      } else if (
        structureValue === EnterpriseStructure.UnincorporatedAssociation ||
        structureValue === EnterpriseStructure.IncorporatedAssociation
      ) {
        enterpriseValues[structureProp] = {
          chairman: excludeFullAddressFromCollection(enterpriseFields.chairman),
          treasurer: excludeFullAddressFromCollection(enterpriseFields.treasurer),
          secretary: excludeFullAddressFromCollection(enterpriseFields.secretary),
        };
      } else if (
        structureValue === EnterpriseStructure.Partnership ||
        structureValue === EnterpriseStructure.ProprietaryLimitedCompany ||
        structureValue === EnterpriseStructure.Trust
      ) {
        enterpriseValues[structureProp] = {
          ...enterpriseFields,
          directors: directorsValue,
          // For Partnership structure, when ownership type is Individual in edit mode, clear beneficialOwners and reset guarantors
          ...(enterpriseFields.ownershipType === OwnershipType.Individual &&
            structureValue === EnterpriseStructure.Partnership && {
              beneficialOwners: null,
              directors: directorsValue?.map(director => ({
                ...director,
                isGuarantor: false,
              })),
            }),
        };
      } else if (structureValue === EnterpriseStructure.SoleTrader || structureValue === EnterpriseStructure.Other) {
        const individual = enterpriseFields.individual
          ? excludeFullAddressFromCollection([enterpriseFields.individual])[0]
          : null;
        enterpriseValues[structureProp] = {
          ...enterpriseFields,
          individual,
        };
      }

      return {
        ...updateApplicationInput,
        applicant: {
          type,
        },
        enterprise: {
          ...enterpriseValues,
          structure,
        },
      };
    }

    case APPLICATION_STEPS.REPAYMENT_ACCOUNT: {
      const {
        payment: { methodId },
        applicant,
      } = stepFormValues;
      const applicantType = getApplicantType(applicant.type);

      return {
        ...updateApplicationInput,
        payment: { methodId },
        applicant: {
          ...applicant,
          type: applicantType,
          termsAndConditions: {
            ...applicant.termsAndConditions,
            directDebitStatement: `${REPAYMENT_ACCOUNT.DIRECT_DEBIT_AGREEMENT} Direct Debit Agreement`,
          },
        },
      };
    }

    case APPLICATION_STEPS.INCOME_REVIEW: {
      // FYI: This is an additional step in the CDR flow
      // used to update the income verification step with Fiskil income details.
      // This ensures the Income Verification step still needs to be completed.
      const applicant = stepFormValues.applicant;
      const { amount, period } = applicant.income.primary;
      const incomeVerification = formValues[ApplicationStep.IncomeVerification];

      const employmentType = incomeVerification?.applicant?.employment?.type
        ? getEnumKey<UpdateApplicationInputEmploymentType, typeof EmploymentType>(
            incomeVerification.applicant.employment.type,
            EmploymentType,
          )
        : null;

      const { employment, ...applicantWithoutEmployment } = incomeVerification?.applicant || {};

      return {
        ...updateApplicationInput,
        step: ApplicationStep.IncomeVerification,
        applicant: {
          ...applicantWithoutEmployment,
          type: applicantType,
          ...(employmentType && {
            employment: {
              ...employment,
              type: employmentType,
            },
          }),
          income: {
            primary: {
              amount,
              period,
            },
          },
          usingOpenBanking: applicant.usingOpenBanking,
        },
      };
    }

    case APPLICATION_STEPS.BENEFICIAL_OWNER: {
      const ownershipDetail = formValues[ApplicationStep.OwnershipDetails];
      const structure: EnterpriseStructureType = getEnumKey(stepFormValues?.enterprise?.structure, EnterpriseStructure);
      const structureProp = pascalToCamelCase(structure);
      const beneficialOwners = excludeFullAddressFromCollection(
        stepFormValues?.enterprise?.[structureProp]?.beneficialOwners ?? [],
      );
      const ownershipType = ownershipDetail?.enterprise?.[structureProp]?.ownershipType;

      return {
        ...updateApplicationInput,
        applicant: {
          type: applicantType,
        },
        enterprise: {
          [structureProp]: {
            ...(ownershipType && { ownershipType }),
            beneficialOwners,
          },
          structure,
        },
      };
    }
    case APPLICATION_STEPS.GUARANTOR_DETAILS: {
      const ownershipDetail = formValues[ApplicationStep.OwnershipDetails];
      const enterprise = stepFormValues?.enterprise || {};
      const structure: EnterpriseStructureType = getEnumKey(enterprise?.structure, EnterpriseStructure);
      const structureProp = pascalToCamelCase(structure);

      const ownershipType = ownershipDetail?.enterprise?.[structureProp]?.ownershipType;
      const guarantors = enterprise?.[structureProp]?.directors ?? [];
      return {
        ...updateApplicationInput,
        applicant: {
          type: applicantType,
        },
        enterprise: {
          [structureProp]: {
            ...(ownershipType && { ownershipType }),
            directors: excludeFullAddressFromCollection(guarantors),
          },
          structure,
        },
      };
    }

    case APPLICATION_STEPS.SUMMARY: {
      const { applicant, payment } = stepFormValues;
      const signatories = (
        formValues as unknown as {
          Signatories?: {
            primarySignatory?: { name?: string; email?: string };
            secondarySignatory?: { name?: string; email?: string };
          };
        }
      ).Signatories;
      const applicantStatus = getEnumKey<UpdateApplicationInputApplicantStatus, typeof ApplicantStatus>(
        ApplicantStatus.Submitted,
        ApplicantStatus,
      );
      const operatorId = getAdminUserId();
      return {
        ...updateApplicationInput,
        status: ApplicationFormStatus.Submitted,
        ...(signatories?.primarySignatory?.email && {
          primarySignatoryEmail: signatories.primarySignatory.email,
        }),
        ...(signatories?.primarySignatory?.name && {
          primarySignatoryName: signatories.primarySignatory.name,
        }),
        ...(signatories?.secondarySignatory?.email && {
          secondarySignatoryEmail: signatories.secondarySignatory.email,
        }),
        ...(signatories?.secondarySignatory?.name && {
          secondarySignatoryName: signatories.secondarySignatory.name,
        }),
        applicant: {
          status: applicantStatus,
          type: getApplicantType(applicantType),
          operatorId,
          ...(applicant?.termsAndConditions && {
            termsAndConditions: {
              capacityToPay: SUMMARY.CONFIRMATION(quote.financedAmount),
            },
          }),
        },
        payment: {
          ...payment,
          ...(payment?.source && { source: getEnumKey(payment.source, PaymentFunds) }),
        },
      };
    }
    default: {
      datadogRum.addError(new Error(`Unknown application step: ${currentPage}.`));
      return null;
    }
  }
}

export { transformCreateApplicationInput, transformCreateJointApplicationInput, transformUpdateApplicationInput };
